/**
 * 현장 위험요인 제안 (안전신문고).
 *
 * A plant hazard, not a software defect. It is deliberately kept apart from the 시스템 개선
 * feedback record: a different collection, a different capability, a different reader. The
 * discriminator `kind: 'field-hazard'` travels with every submission so the two can never be
 * confused once they reach the server.
 */

export type HazardType =
  | 'fall'
  | 'caught'
  | 'struck'
  | 'electric'
  | 'fire-explosion'
  | 'chemical'
  | 'slip'
  | 'equipment'
  | 'other';

export type HazardImmediateAction = 'none' | 'reported' | 'isolated' | 'stopped';

export const hazardTypes: ReadonlyArray<readonly [HazardType, string]> = [
  ['fall', '떨어짐'],
  ['caught', '끼임'],
  ['struck', '부딪힘·맞음'],
  ['electric', '감전'],
  ['fire-explosion', '화재·폭발'],
  ['chemical', '질식·중독'],
  ['slip', '넘어짐'],
  ['equipment', '설비 결함'],
  ['other', '그 밖의 위험'],
];

export const hazardImmediateActions: ReadonlyArray<readonly [HazardImmediateAction, string]> = [
  ['none', '조치 없음'],
  ['reported', '관리자에게 알림'],
  ['isolated', '주변 통제'],
  ['stopped', '작업 중지'],
];

export type HazardReportDraft = {
  hazardType: HazardType;
  location: string;
  description: string;
  immediateAction: HazardImmediateAction;
};

export const initialHazardReportDraft: HazardReportDraft = {
  hazardType: 'other',
  location: '',
  description: '',
  immediateAction: 'none',
};

export type HazardReportSubmission = {
  hazardReportId: string;
  report: {
    hazardType: HazardType;
    location: string;
    description: string;
    immediateAction: HazardImmediateAction;
    occurredAt: string;
  };
};

export const hazardReportLocationLimit = 200;
export const hazardReportDescriptionLimit = 2_000;

/**
 * The first sentence a person cannot submit without. Returned rather than thrown so the form can
 * hold the primary action closed and say why, instead of failing only once the action is pressed.
 */
export function hazardReportBlockingReason(draft: HazardReportDraft): string | null {
  if (!draft.location.trim()) return '위험을 발견한 위치를 적어 주세요.';
  if (draft.location.trim().length > hazardReportLocationLimit)
    return `위치는 ${hazardReportLocationLimit}자까지 적을 수 있습니다.`;
  if (!draft.description.trim()) return '어떤 위험인지 적어 주세요.';
  if (draft.description.trim().length > hazardReportDescriptionLimit)
    return `위험 내용은 ${hazardReportDescriptionLimit}자까지 적을 수 있습니다.`;
  return null;
}

/**
 * Builds the exact `report` object the server contract accepts. No client context, no viewport,
 * no diagnostics: a hazard is about the plant, so nothing about the device belongs in it.
 */
export function buildHazardReportSubmission(
  draft: HazardReportDraft,
  input: { now: string },
): HazardReportSubmission {
  const blocked = hazardReportBlockingReason(draft);
  if (blocked) throw new Error(blocked);
  return {
    hazardReportId: `hazard-${input.now.replace(/\D/gu, '').slice(0, 14)}`,
    report: {
      hazardType: draft.hazardType,
      location: draft.location.trim(),
      description: draft.description.trim(),
      immediateAction: draft.immediateAction,
      occurredAt: input.now,
    },
  };
}
